import React, {useEffect, useState} from 'react'
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    styled,
    TextField,
    Tooltip,
} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {addCheckedRule, addLocalToData, removeCheckedRule,} from "../features/validationRuleSlice";
import ValidationRuleDialog from "./ValidationRuleDialog";
import {auto} from "html-webpack-plugin/lib/chunksorter";
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {columnLabel, v3NameOf} from "../utils/column-aliases";

const StyledGrid = styled(Grid)({
    maxHeight: '180px',
    paddingBottom: '20px',
    animation: 'section-appear 2s',
    '@keyframes section-appear': {
        '0%': {
            opacity: 0,
            transform: 'translateY(10%)'
        },
        '100%': {
            opacity: 1,
        }
    }
})
const StyledFuzzyTextField = styled(TextField)({
    '& input': {
        height: '15px',
        // width: '90px',
    },
    '& label.Mui-focused': {
        fontSize: '14px'
    },
    '& label': {
        fontSize: '14px'
    }
})
const StyledSectionDiv = styled('div')({
    padding: '15px 0 10px 0',
    borderRadius: '10px',
    boxShadow: '3px 3px 3px #e0e0e0',
    border: '1.5px solid #e0e0e0'
})

export default function ValidationRuleSection(props) {

    const {
        name,
        errorName,
        primaryField,
        description,
        selected,
        eccrEnabled,
        detailsAsMap,
        dependsOnAsSet,
        ruleId,
        value,
        fuzzyMatchRate,
    } = props.rule;
    const {setIsChanged, onRefresh, initialRuleValue} = props;

    /**
     * Which per-rule controls to show.
     *
     * The details JSON (AllowValue / AllowEccr / AllowFuzzyMatch) is portal metadata only -- no
     * server-side code reads it. The real runtime gates are the eccrEnabled, value and
     * fuzzyMatchRate columns. Gating the UI on the JSON alone meant a rule could correct a cell on
     * every run with no control on this screen: eccrEnabled = 1 with AllowEccr absent or false hid
     * the toggle, so an admin could neither see the correction was happening nor switch it off.
     *
     * So show a control when the flag says to, OR when the database says the rule already does the
     * thing. Falling back to the column keeps the screen honest about what the rule actually does,
     * which is the point of the control.
     */
    const canEditEccr = Boolean(detailsAsMap?.AllowEccr) || Boolean(eccrEnabled);
    const canEditValue = Boolean(detailsAsMap?.AllowValue) || Boolean(value);
    const canEditFuzzy = Boolean(detailsAsMap?.AllowFuzzyMatch)
        || (Number(fuzzyMatchRate) > 0 && Number(fuzzyMatchRate) < 100);
    const [eccrOpen, setEccrOpen] = useState(eccrEnabled);
    const [selectedColor, setSelectedColor] = useState(selected ? {background: 'none'} : {background: '#ececec'})
    const [onSelected, setSelected] = useState(selected);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogInfo, setDialogInfo] = useState([])
    const [checkbox, setCheckbox] = useState(false);
    const [validFuzzyRate, setValidFuzzyRate] = useState(true);

    const [first, setFirst] = useState(true);
    const [ruleData, setRuleData] = useState(initialRuleValue || {})

    const {checkedRulesDict} = useSelector(state => state.validationRules);
    const dispatch = useDispatch();

    useEffect(() => {
        setSelected(selected);
        setSelectedColor(selected ? {background: 'none'} : {background: '#ececec'})
    }, [selected])

    useEffect(() => {
        setRuleData(initialRuleValue);
    }, [onRefresh])

    /** when user stop input after 1.5 second, save the value to redux store */
    useEffect(() => {
        if (first) {
            // don't render the component the first time
            setFirst(false);
        } else {
            const timer = setTimeout(() => {
                dispatch(addLocalToData({'name': name, 'value': ruleData}))
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [ruleData])

    const handleEccrCheckboxChange = (event) => {
        const isChecked = event.target.checked;
        if (!isChecked) {
            setEccrOpen(false);
        } else {
            setEccrOpen(true);
        }
        setIsChanged(true);
        setRuleData({
            ...ruleData,
            eccrEnabled: isChecked
        })
    }

    const handleInput = (event) => {
        const {value, name} = event.target;
        setIsChanged(true);

        if (name === 'fuzzyMatchRate') {
            return handleFuzzyMatchRateChange(value);
        }

        setRuleData({
            ...ruleData,
            [name]: value
        })
    }

    const handleFuzzyMatchRateChange = (value) => {
        const isDigit = /^\d+$/.test(value);
        const num = parseInt(value);

        if (isNaN(num)) {
            setRuleData({
                ...ruleData,
                "fuzzyMatchRate": 0
            })
            setValidFuzzyRate(false);
            setIsChanged(true);
            return;
        }

        if (!isDigit) {
            setValidFuzzyRate(false)
        } else {
            if (Number.isInteger(num) && num >= 1 && num <= 100) {
                setValidFuzzyRate(true);
            } else {
                setValidFuzzyRate(false);
            }
        }
        setRuleData({
            ...ruleData,
            "fuzzyMatchRate": num
        })
    }

    const handleSelectedCheckboxChange = (event) => {
        setIsChanged(true);
        const checkedRulesMap = new Map();
        Object.entries(checkedRulesDict).forEach(([key, value]) => checkedRulesMap.set(key, value));

        if (!event.target.checked) {

            // situation 1: rule does not depend on other rules and no rules depended on this rule.
            if (checkedRulesMap.get(name).length === 0) {
                dispatch(removeCheckedRule({'selectedRuleName': name, 'selectedDependants': dependsOnAsSet}));
                setSelectedColor({backgroundColor: 'none'})
                setSelected(false);
                setRuleData({...ruleData, 'selected': false});

                // situation 2: rule does not depend on other rules but there are rules depended on this rule
            } else {
                const dependantsNeedToUnchecked = checkedRulesMap.get(name).filter(dep => {
                    return checkedRulesMap.has(dep);
                })

                const secondLayerDependants = []
                dependantsNeedToUnchecked.map(dep => {
                    const depArr = checkedRulesMap.get(dep);
                    if (depArr.length !== 0) {
                        depArr.map(rn => {
                            if (checkedRulesMap.has(rn)) {
                                secondLayerDependants.push(rn);
                            }
                        })
                    }

                })

                dependantsNeedToUnchecked.push(...secondLayerDependants);

                // Open dialog and display information
                console.log(dependantsNeedToUnchecked);
                setDialogInfo(dependantsNeedToUnchecked);
                setCheckbox(false);
                setDialogOpen(true);
            }

        } else {
            if (dependsOnAsSet.length === 0) {
                dispatch(addCheckedRule({'selectedRuleName': name, 'selectedDependants': []}))
                setSelectedColor({backgroundColor: 'none'})
                setSelected(true);
                setRuleData({...ruleData, 'selected': true});
                return;
            }

            // if dependsOnAsSet is not empty, check if one or more rules in this set are not been selected.
            const missingRules = dependsOnAsSet.filter(ruleName => {
                return !checkedRulesMap.has(ruleName);
            })

            if (missingRules.length > 0) {
                // Open dialog and display information
                console.log(missingRules);
                setDialogInfo(missingRules);
                setCheckbox(true);
                setDialogOpen(true);
                return;
            }
            // all rules in dependsOnAsSet are being selected
            dispatch(addCheckedRule({'selectedRuleName': name, 'selectedDependants': dependsOnAsSet}))
            setSelectedColor({backgroundColor: 'none'})
            setSelected(true);
            setRuleData({...ruleData, 'selected': true});
        }

    }

    return (
        <StyledGrid item xs={12} md={12} lg={12}>
            <StyledSectionDiv sx={selectedColor}>
                <ValidationRuleDialog dialogOpen={dialogOpen} setDialogOpen={(d) => setDialogOpen(d)}
                                      dialogInfo={dialogInfo} checkbox={checkbox} ruleName={name}
                                      setRuleData={(e) => setRuleData(e)}/>
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <Checkbox onChange={handleSelectedCheckboxChange} checked={onSelected}/>
                    <div>
                        <span style={{fontWeight: 'bold', color: '#666666', fontSize: 14}}>
                            <Tooltip title='rule ID' arrow placement='top'><span>#{ruleId} </span></Tooltip>

                            {name} / {errorName}
                            {primaryField &&
                                <Tooltip
                                    title={v3NameOf(primaryField)
                                        ? `Renamed in v3.0. A v2.0 file calls this column ${primaryField}; a v3.0 file calls it ${v3NameOf(primaryField)}. Both spellings are accepted on upload, and the column keeps its position. Under SOW #4 it describes the Location -- the Organization is derived from the endpoint certificate, not from the CSV.`
                                        : 'The CSV column this rule reads'}
                                    arrow placement='top'>
                                    <span style={{fontSize: 12}}> (PrimaryField: {columnLabel(primaryField)})</span>
                                </Tooltip>}


                        </span>
                        &emsp;
                        {/*<span style={{fontWeight: 'bold', color: 'rgba(60,120,185,0.8)', fontSize: 17}}>*/}
                        {/*    {name}*/}
                        {/*    <span style={{color: 'rgba(60,120,185,0.8)', fontSize: 15}}> ({errorName})</span>*/}
                        {/*</span> &emsp;*/}
                        <span
                            style={{fontSize: '12px'}}>{`${dependsOnAsSet.length ? "Depends on: ( " + dependsOnAsSet + " )" : ""}`}</span>
                    </div>

                    {/* Severity select button */}
                    <FormControl sx={{ml: 'auto', mr: '8px', minWidth: '140px'}} size='small'>
                        <InputLabel id="severity-select-label">Severity</InputLabel>
                        <Select
                            labelId="severity-select-label"
                            label="Severity"
                            value={ruleData.severity}
                            onChange={handleInput}
                            disabled={!onSelected}
                            name='severity'
                        >
                            <MenuItem value='Error'>Error</MenuItem>
                            <MenuItem value='Warning'>Warning</MenuItem>
                            <MenuItem value='Alert'>Alert</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Tolerated select button */}
                    <FormControl sx={{mr: '16px', minWidth: '140px'}} size='small'>
                        <InputLabel id="tolerated-select-label">Tolerated</InputLabel>
                        <Select
                            labelId="tolerated-select-label"
                            value={ruleData.tolerated}
                            label="Tolerated"
                            onChange={handleInput}
                            disabled={!onSelected}
                            name='tolerated'
                        >
                            <MenuItem value='true'>True</MenuItem>
                            <MenuItem value='false'>False</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{marginLeft: 5, mr: 2}}>
                    <span style={{color: '#444444', fontSize: 14}}>{description}</span>
                    {/* Details for value, eccrValue and fuzzyRate*/}
                    <Grid container marginTop='10px' gap='5px'>
                        {canEditValue &&
                            <Grid item xs={4} sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                                <span>Value: </span>
                                <TextField hiddenLabel variant="filled" size="small" sx={{width: '100%'}}
                                           disabled={!onSelected}
                                           onChange={handleInput}
                                           autoComplete='off'
                                           value={ruleData.value}
                                           name='value'
                                />
                            </Grid>
                        }
                        {canEditFuzzy &&
                            <Grid item xs={2} sx={{display: 'flex', alignItems: 'center'}}>
                                <StyledFuzzyTextField
                                    error={!validFuzzyRate}
                                    label='Match % Requirement (1 - 100)'
                                    value={ruleData.fuzzyMatchRate}
                                    variant='filled'
                                    autoComplete='off'
                                    placeholder='1 - 100'
                                    onChange={handleInput}
                                    size='small'
                                    disabled={!onSelected || !canEditFuzzy}
                                    name='fuzzyMatchRate'
                                />
                            </Grid>
                        }
                        {canEditEccr &&
                            <Grid item xs={5} style={{display: 'flex', gap: 1, alignItems: 'center'}}>
                                <FormControlLabel control={<Checkbox onChange={handleEccrCheckboxChange}
                                                                     disabled={!onSelected}
                                                                     checked={eccrOpen}
                                                                     icon={<RadioButtonUncheckedIcon/>}
                                                                     checkedIcon={<RadioButtonCheckedIcon/>}/>}
                                                  label='ECCR:' sx={{ml: 0, mr: 0}}
                                />
                                <TextField hiddenLabel={true} variant='filled' size='small' sx={{width: '100%'}}
                                           disabled={!onSelected || !eccrOpen}
                                           value={ruleData.eccrValue}
                                           autoComplete='off'
                                           onChange={handleInput}
                                           name='eccrValue'
                                />
                            </Grid>
                        }

                    </Grid>
                </Box>
            </StyledSectionDiv>
        </StyledGrid>
    )
}
